
import React from 'react';
import { Product } from '../types';
import { X, Printer, Download, UtensilsCrossed, MapPin, Instagram, Mail, MessageCircle } from 'lucide-react';

interface BrochureProps {
  logoUrl: string;
  kidsLine: Product[];
  traditionLine: Product[];
  onClose: () => void;
}

// Componente simple para el icono de WhatsApp para el Brochure
const WhatsAppIcon = ({ size = 14 }: { size?: number }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Brochure: React.FC<BrochureProps> = ({ logoUrl, kidsLine, traditionLine, onClose }) => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-white overflow-y-auto animate-in fade-in zoom-in-95 duration-300 print:p-0">
      {/* Barra de herramientas (Oculta al imprimir) */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-stone-100 p-4 flex justify-between items-center z-50 print:hidden">
        <div className="flex items-center gap-4">
          <button 
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors"
          >
            <X size={24} className="text-stone-600" />
          </button>
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-stone-400">Catálogo Digital v3.0</span>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handlePrint}
            className="flex items-center gap-2 bg-stone-900 text-white px-6 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest hover:bg-stone-700 transition-all shadow-lg"
          >
            <Printer size={16} /> Imprimir / Guardar PDF
          </button>
        </div>
      </div>

      {/* Contenido del Folleto */}
      <div className="max-w-[800px] mx-auto bg-white shadow-2xl my-8 print:my-0 print:shadow-none">
        
        {/* PORTADA REDISEÑADA */}
        <div className="bg-[#2a1f1a] text-white p-20 text-center relative overflow-hidden h-[1000px] flex flex-col justify-center items-center">
          {/* Nueva Imagen Maestra de Marca */}
          <div className="relative z-10 mb-12 animate-in zoom-in duration-1000">
            <div className="w-64 h-64 mx-auto rounded-full border-4 border-amber-400/30 p-2 overflow-hidden shadow-2xl">
                <img src={logoUrl} alt="Arte-Sano Brand" className="w-full h-full object-cover rounded-full" />
            </div>
            <h1 className="text-7xl font-serif mt-12 font-bold tracking-tight text-amber-50">Arte-Sano</h1>
            <p className="text-[14px] uppercase tracking-[0.6em] mt-6 font-bold text-amber-400">Panadería y Repostería</p>
          </div>
          
          <div className="w-16 h-[2px] bg-amber-400 mb-10 z-10"></div>
          
          <div className="z-10 text-center">
            <h2 className="text-4xl font-serif italic mb-4 text-stone-200">Catálogo de Temporada</h2>
            <p className="text-stone-400 text-sm font-light tracking-[0.3em] uppercase">Cartagena • Santa Marta • 2024</p>
          </div>
          
          <div className="absolute bottom-20 left-0 right-0 text-[10px] uppercase tracking-[0.4em] font-bold opacity-30 z-10">
            Artesanía • Nutrición • Conciencia • Alma
          </div>

          {/* Elementos decorativos de fondo */}
          <div className="absolute top-0 right-0 w-full h-full opacity-10">
            <img src={logoUrl.split('&w=')[0] + '&w=50'} className="w-full h-full object-cover blur-xl" alt="Background decoration" loading="lazy" />
          </div>
        </div>

        {/* PÁGINA 1: PEQUEÑOS GIGANTES */}
        <div className="p-16 border-b border-stone-100 print:break-after-page">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-amber-600 text-[10px] font-bold uppercase tracking-[0.3em]">Colección Infantil</span>
              <h3 className="text-4xl font-bold text-stone-900 mt-2">Pequeños Gigantes</h3>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Nutrición de origen</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-12 gap-y-16">
            {kidsLine.map((product) => (
              <div key={product.id} className="flex flex-col">
                <div className="aspect-video rounded-xl overflow-hidden mb-4 bg-stone-100 shadow-sm border border-stone-100">
                  <img src={product.image} className="w-full h-full object-cover" alt={product.name} loading="lazy" />
                </div>
                <h4 className="text-lg font-bold text-stone-900 mb-1">{product.name}</h4>
                <p className="text-xs text-stone-500 mb-3 leading-relaxed font-light">{product.description}</p>
                <div className="mt-auto">
                  <div className="flex items-center gap-2 text-[9px] font-bold text-emerald-700 uppercase tracking-tighter bg-emerald-50 px-2 py-1 rounded w-fit">
                    <UtensilsCrossed size={10} /> {product.differentialValue}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PÁGINA 2: TRADICIÓN */}
        <div className="p-16">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-amber-600 text-[10px] font-bold uppercase tracking-[0.3em]">Colección Esencial</span>
              <h3 className="text-4xl font-bold text-stone-900 mt-2">Tradición Local</h3>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Sabor de nuestra tierra</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-10 gap-y-12">
            {traditionLine.map((product) => (
              <div key={product.id} className="flex flex-col group">
                <div className="aspect-[4/3] rounded-xl overflow-hidden mb-4 bg-stone-100 shadow-sm border border-stone-100">
                  <img src={product.image} className="w-full h-full object-cover" alt={product.name} loading="lazy" />
                </div>
                <div className="flex justify-between items-start mb-1">
                  <h4 className="text-lg font-bold text-stone-800">{product.name}</h4>
                  <span className="text-[8px] font-bold uppercase tracking-widest text-amber-700 px-2 py-0.5 bg-amber-50 rounded border border-amber-100">
                    {product.differentialValue}
                  </span>
                </div>
                <p className="text-[11px] text-stone-500 italic font-light leading-relaxed">
                  {product.description}
                </p>
              </div>
            ))}
          </div>

          {/* FOOTER DEL FOLLETO */}
          <div className="mt-24 pt-12 border-t border-stone-200 flex flex-col items-center">
            {/* Logo en el footer del brochure */}
            <div className="w-16 h-16 rounded-full overflow-hidden border border-stone-200 mb-8 grayscale hover:grayscale-0 transition-all">
                <img src={logoUrl} className="w-full h-full object-cover" alt="Footer Logo" loading="lazy" />
            </div>
            <div className="flex flex-wrap justify-center gap-8 mb-8 text-stone-400">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest"><MapPin size={14} /> Cartagena</div>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-stone-600">
                <WhatsAppIcon size={14} /> +57 312 395 9838
              </div>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest"><Instagram size={14} /> @artesanopan</div>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                <a href="mailto:contact@arte-sano.store" className="hover:text-stone-900 transition-colors flex items-center gap-2">
                  <Mail size={14} /> contact@arte-sano.store
                </a>
              </div>
            </div>
            <p className="text-[9px] text-stone-300 uppercase tracking-[0.4em] font-bold">
              Pan con Conciencia y Alma • Arte-Sano © 2024
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Brochure;
